import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Button, ButtonProps } from '@mui/material';
import { AuthContext } from 'index';
import InfoInviteModal from 'modals/InfoInviteModal';
import InviteModal from 'modals/InviteModal';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setOpenedInviteModal } from 'store/globalSlice';
import { RootState } from 'store/store';
import { colors } from 'styles/colors';
import { ParticipantAPI } from 'utils/ParticipantAPI';
import { ViewState } from 'utils/viewstate';

const ColorButtonConfirm = styled(Button)<ButtonProps>(() => ({
    color: 'white',
    backgroundColor: colors.primaryButtonBackground,
    width: '6rem',
    height: '2.4rem',
    fontSize: '14px',
    fontWeight: '400',
    fontFamily: 'Figtree',
    padding: '5px',
    textTransform: 'none',
    '&:hover': {
        backgroundColor: colors.primaryButtonBackground,
    },
    borderRadius: '0.5rem',
}));

const DenyAccessPage = () => {
    const dispatch = useDispatch();
    const participantViewState = useSelector((state: RootState) => state.user.participantViewState);
    const token = useSelector((state: RootState) => state.global.authToken);
    const [isOpenInfoModal, setIsOpenInfoModal] = useState<boolean>(false);
    const [textForInfoModal, setTextForInfoModal] = useState<string>('1');
    const auth = useContext(AuthContext);

    const invite = useCallback(async () => {
        try {
            await ParticipantAPI.inviteUser(token);
            console.log('Response - ', participantViewState);

            setTextForInfoModal(
                'Tak. Vi har modtaget din registrering og du vil indenfor et par timer modtage en invitation som Digital Post. Hvis du efter at have læst invitationen ønsker at deltage, skal du klikke på linket, som fører dig til vores hjemmeside, hvorfra du nu kan tilmelde dig.'
            );
            setIsOpenInfoModal(true);
            dispatch(setOpenedInviteModal(false));
        } catch (e: any) {
            setTextForInfoModal(e.response.data.message);
            setIsOpenInfoModal(true);
            dispatch(setOpenedInviteModal(false));
            console.log('Error - ', e.response);
        }
    }, [dispatch, participantViewState, token]);

    const checkIfCanInviteUser = useCallback(async () => {
        try {
            await ParticipantAPI.getCanInviteUser(token);
            if (participantViewState === ViewState.DENY_ACCESS_1) {
                dispatch(setOpenedInviteModal(true));
            }
        } catch (e: any) {
            setTextForInfoModal(e.response.data.message);
            setIsOpenInfoModal(true);
            console.log('Error - ', e.response.data);
        }
    }, [dispatch, participantViewState, token]);

    useEffect(() => {
        if (participantViewState === ViewState.DENY_ACCESS_1) {
            checkIfCanInviteUser();
        }
    }, [checkIfCanInviteUser, participantViewState]);

    const onLogout = useCallback(() => {
        localStorage.removeItem('id_token');
        if (process.env.NODE_ENV !== 'development') {
            localStorage.setItem('logoutCheck', Date.now().toString());
        }
        auth.logout({
            redirectUri: process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://planetdiet.dk/',
        });
    }, [auth]);

    return (
        <div className={styles.content}>
            <InviteModal invite={invite} />
            <InfoInviteModal open={isOpenInfoModal} text={textForInfoModal} />
            <div className={styles.block}>
                {
                    (participantViewState === ViewState.DENY_ACCESS_1) &&
                    'Tak fordi du viser interesse i PlanetDiet-projektet! For at kunne deltage, skal du først inviteres via et brev i din Digitale Postkasse.'
                }
                {
                    participantViewState === ViewState.DENY_ACCESS_2 &&
                    'Vi har registreret din tilmelding til informations-mødet, hvor studiet bliver gennemgået med dig. Vend venligst tilbage når du har været til dette møde.'
                }
                {
                    participantViewState === ViewState.DENY_ACCESS_3 &&
                    'Baseret på dit svar i spørgeskemaet har vi noteret, at du ikke kan deltage i PlanetDiet-projektet. Vi takker dig for din interesse!'
                }
                {
                    participantViewState === ViewState.DENY_ACCESS_4 &&
                    'Vi har registreret din tilmelding til screening-mødet, hvor vi vurderer om du er egnet til studiet. Først når du har været til dette møde samt underskrevet din samtykkeerkæring, kan du få adgang til at booke næste klinikbesøg.'
                }
                {
                    participantViewState === ViewState.DENY_ACCESS_5 &&
                    <>
                        <div>
                        Vi har ikke endnu registreret din samtykkeerkæring. Først når den er modtaget kan du booke næste klinikbesøg. Kontakt venligst studiets personale hvis du har spørgsmål til dette.
                        </div>
                        <div>
                            <div className={styles.button}>
                                <ColorButtonConfirm onClick={onLogout} variant="contained">
                                    Afslut
                                </ColorButtonConfirm>
                            </div>
                        </div>
                    </>
                }
            </div>
        </div>
    );
};

const styles = {
    content: css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '7rem',
    }),
    block: css({
        paddingLeft: '1.25rem',
        paddingRight: '1.25rem',
    }),
    button: css({
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        fontFamily: 'Figtree',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal',
        marginTop: '1rem',
    }),
};

export default DenyAccessPage;
